import React, { useMemo, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import "./FillwordQuestion.css";

const normalizeWord = (word) =>
  String(word || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("uk-UA");

const FillwordQuestion = ({
  question,
  imgPath,
  correctWords,
  onWordFound,
  showNextButton,
  onNext,
  showFinishButton,
  onFinish,
}) => {
  const [value, setValue] = useState("");
  const [foundWords, setFoundWords] = useState([]);
  const [message, setMessage] = useState("");

  const normalizedWords = useMemo(() => {
    const source = Array.isArray(correctWords) ? correctWords : [];
    const unique = [];
    const seen = new Set();

    source.forEach((word) => {
      const label = String(word || "").trim();
      const key = normalizeWord(label);
      if (!label || seen.has(key)) return;
      seen.add(key);
      unique.push({ label, key });
    });

    return unique;
  }, [correctWords]);

  const foundKeys = useMemo(() => new Set(foundWords.map((word) => word.key)), [foundWords]);
  const remaining = Math.max(0, normalizedWords.length - foundWords.length);
  const solved = normalizedWords.length > 0 && remaining === 0;
  const canContinue = foundWords.length > 0;

  const submitWord = () => {
    const key = normalizeWord(value);
    if (!key || solved) return;

    const matched = normalizedWords.find((word) => word.key === key);
    if (!matched) {
      setMessage("Такого слова немає у списку.");
      return;
    }

    if (foundKeys.has(key)) {
      setMessage("Це слово вже знайдено.");
      setValue("");
      return;
    }

    const nextFoundWords = [...foundWords, matched];
    setFoundWords(nextFoundWords);
    setValue("");
    setMessage(nextFoundWords.length === normalizedWords.length ? "Усі слова знайдено." : "Правильно.");
    onWordFound?.();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitWord();
    }
  };

  return (
    <div className="fillword-question">
      {question && (
        <div className="fillword-question-text" dangerouslySetInnerHTML={{ __html: question }} />
      )}

      <div className="fillword-layout">
        <div className="fillword-image-frame">
          {imgPath ? (
            <img src={imgPath} alt="Філворд" className="fillword-image" />
          ) : (
            <div className="fillword-empty-image">Зображення філворда не додано</div>
          )}
        </div>

        <div className="fillword-panel">
          <div className="fillword-counter">
            Залишилось знайти: <strong>{remaining}</strong>
          </div>

          <div className="fillword-input-row">
            <input
              type="text"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Введіть слово"
              className="fillword-input"
            />
            <button type="button" className="fillword-submit" onClick={submitWord} disabled={!value.trim()}>
              <SearchIcon fontSize="small" />
            </button>
          </div>

          {message && (
            <div className={`fillword-message${message.includes("немає") ? " is-error" : ""}`}>
              {message}
            </div>
          )}

          <div className="fillword-found-title">Знайдені слова</div>
          <div className="fillword-found-list">
            {foundWords.length ? (
              foundWords.map((word) => (
                <span className="fillword-found-chip" key={word.key}>
                  <CheckIcon fontSize="inherit" />
                  {word.label}
                </span>
              ))
            ) : (
              <span className="fillword-found-empty">Поки що немає</span>
            )}
          </div>

          {showNextButton && canContinue && (
            <button type="button" className="btn nextAnswer" onClick={onNext} style={{ display: "inline-block" }}>
              Далі
            </button>
          )}

          {showFinishButton && canContinue && (
            <button type="button" className="btn nextAnswer" onClick={onFinish} style={{ display: "inline-block" }}>
              Завершити
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FillwordQuestion;
